interface SidebarProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
  userName: string;
  onLogout: () => void;
}

const navItems = [
  { id: 'manager', icon: 'ri-user-heart-line', label: 'Мой менеджер' },
  { id: 'schedule', icon: 'ri-calendar-line', label: 'Расписание' },
  { id: 'projects', icon: 'ri-folder-open-line', label: 'Мои проекты' },
  { id: 'payments', icon: 'ri-wallet-3-line', label: 'Мои выплаты' },
  { id: 'requisites', icon: 'ri-file-list-3-line', label: 'Мои реквизиты' },
  { id: 'calculator', icon: 'ri-calculator-line', label: 'Калькулятор ЗП' },
];

export default function Sidebar({ activeSection, onSectionChange, userName, onLogout }: SidebarProps) {
  const initials = userName
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('');

  return (
    <aside className="w-72 flex-shrink-0 bg-[#0d0f18] border-r border-[#1e2130] flex flex-col min-h-screen">
      {/* Brand */}
      <div className="px-6 py-5 border-b border-[#1e2130] flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-emerald-500/10 border border-emerald-500/20">
          <i className="ri-shield-user-line text-emerald-400 text-lg" />
        </div>
        <div className="flex flex-col leading-tight">
          <span className="text-[10px] text-slate-500 tracking-widest uppercase">Корпоративный</span>
          <span className="text-sm font-semibold text-white">Портал сотрудников</span>
        </div>
      </div>

      {/* Profile block */}
      <div className="px-6 py-6 border-b border-[#1e2130]">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold text-lg select-none">
              {initials}
            </div>
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-400 rounded-full border-2 border-[#0d0f18]" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white truncate">{userName}</p>
            <p className="text-xs text-slate-500 mt-0.5">Оператор чата</p>
          </div>
        </div>

        {/* Status badge */}
        <div className="mt-4 flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-xl px-3 py-2.5">
          <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <i className="ri-information-line text-amber-400 text-sm" />
          </span>
          <span className="text-xs text-amber-400 leading-tight">
            Процесс трудоустройства ещё не завершен, часть функционала недоступна
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = activeSection === item.id;
          return (
            <button
              key={item.id}
              onClick={() => onSectionChange(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                isActive
                  ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-[#13161f]'
              }`}
            >
              <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-base`} />
              </span>
              {item.label}
              {item.id === 'requisites' && (
                <span className="ml-auto w-4 h-4 flex items-center justify-center">
                  <i className="ri-lock-line text-xs text-slate-600" />
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="px-4 py-4 border-t border-[#1e2130]">
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-slate-500 hover:text-slate-300 hover:bg-[#13161f] transition-all duration-200 cursor-pointer whitespace-nowrap"
        >
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-logout-box-r-line" />
          </span>
          Выйти из аккаунта
        </button>
      </div>

      {/* Bottom branding */}
      <div className="px-6 py-4 border-t border-[#1e2130]">
        <div className="relative">
          <div className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/20 to-transparent" />
        </div>
        <p className="text-[10px] text-slate-600 tracking-widest uppercase">© 2026 · Корпоративный портал</p>
      </div>
    </aside>
  );
}
